import { cn } from '@neo/lib/utils'
import { Interweave } from 'interweave'
import { EmailMatcher } from 'interweave-autolink'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { CustomUrlMatcher } from 'v2/utils'
import { telMatcher } from '../matchers/tel-matcher'

interface FormattedMessageProps {
  content: string
  oneLine?: boolean
  noFormatting?: boolean
  className?: string
  markdown?: boolean
  disallowedElements?: string[]
}

export const FormattedMessage = ({
  content,
  oneLine = false,
  noFormatting = false,
  className = '',
  markdown = false,
  disallowedElements = ['ul', 'ol', 'li'],
}: FormattedMessageProps) => {
  const displayContent = oneLine ? content.split('\n')[0] : content

  if (noFormatting) return <span className={className}>{displayContent}</span>

  if (markdown) {
    // Escape numeric list markers only when lists are disallowed (digit-dot followed by space/EOL)
    const listsDisallowed = disallowedElements?.some((e) => e === 'ol' || e === 'ul' || e === 'li')
    const sanitizedContent = listsDisallowed
      ? displayContent.replace(/^(\s*)(\d+)\.(?=\s|$)/gm, '$1$2\\.')
      : displayContent
    return (
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        disallowedElements={disallowedElements}
        unwrapDisallowed
        components={{
          a: ({ node, ...props }) => (
            <a
              {...props}
              className={cn('underline break-all', className)}
              target="_blank"
              rel="noopener noreferrer"
            />
          ),
          h1: ({ node, ...props }) => <div {...props} className={cn('text-h1-500', className)} />,
          h2: ({ node, ...props }) => <div {...props} className={cn('text-h2-500', className)} />,
          h3: ({ node, ...props }) => <div {...props} className={cn('text-h3-500', className)} />,
          h4: ({ node, ...props }) => <div {...props} className={cn('text-h4-500', className)} />,
          h5: ({ node, ...props }) => <div {...props} className={cn('text-p-600', className)} />,
          h6: ({ node, ...props }) => <div {...props} className={cn('text-p-500', className)} />,
          p: ({ node, ...props }) => (
            <div {...props} className={cn('text-p-400 mb-4 last:mb-0', className)} />
          ),
          ol: ({ node, ...props }) => (
            <ol
              {...props}
              className={cn('text-p-400 mb-4 last:mb-0 list-decimal list-inside', className)}
            />
          ),
          ul: ({ node, ...props }) => (
            <ul
              {...props}
              className={cn('text-p-400 mb-4 last:mb-0 list-disc list-inside', className)}
            />
          ),
          li: ({ node, ...props }) => (
            <li {...props} className={cn('text-p-400 mb-2 list-inside', className)} />
          ),
        }}
      >
        {sanitizedContent}
      </ReactMarkdown>
    )
  }

  return (
    <Interweave
      className={cn('text-p-400 [&_a]:underline [&_a]:break-all', className)}
      content={displayContent}
      matchers={[new EmailMatcher('email'), new CustomUrlMatcher('url'), telMatcher]}
    />
  )
}
